import type {
  DocumentReaderUploadStatus,
  IDocumentReaderUploadLimits,
} from '@commenda/api/commenda-ai/document-reader/types';
import type { CommendaAiUploadFile } from '@commenda/api/commenda-ai/types';

export enum DataRoomFileState {
  UPLOADING = 'UPLOADING',
  STORED = 'STORED',
  FAILED = 'FAILED',
}

/** One local file of a data room, paired with the file the backend created for it. */
export interface IDataRoomLocalFile {
  selectedFile: CommendaAiUploadFile;
  fileId: string;
  batchIndex: number;
  partCount: number;
  partSizeBytes: number;
  /** Part number to the ETag its PUT answered, for every part S3 holds. */
  storedEntityTags: Map<number, string>;
}

export interface IDataRoomFileProgress {
  fileId: string;
  name: string;
  sizeBytes: number;
  sentBytes: number;
  state: DataRoomFileState;
  error: string | null;
}

export interface IDataRoomUploadProgress {
  files: IDataRoomFileProgress[];
  sentBytes: number;
  totalBytes: number;
  isStored: boolean;
}

/** Kept in sessionStorage while a room uploads. Only ids and numbers: no names and no part URLs. */
export interface IDataRoomResumeRecord {
  uploadId: string;
  fileCount: number;
  /** Last-modified time of each file, by batch and position, so a re-selected file is the same file. */
  lastModifiedByBatch: number[][];
}

export interface IDataRoomPartRange {
  partNumber: number;
  start: number;
  end: number;
}

export interface IDataRoomResumeMatch {
  files: IDataRoomLocalFile[];
  unmatchedNames: string[];
}

/** Runs a task once one of a fixed number of slots is free. */
export type TDataRoomLimiter = <T>(task: () => Promise<T>) => Promise<T>;

export interface IDataRoomTransferContext {
  uploadId: string;
  maxPartUrlsPerRequest: number;
  signal: AbortSignal;
  /** Shared by every file of the room, so no more than PARTS_IN_FLIGHT PUTs run at once. */
  limitPart: TDataRoomLimiter;
  onFileBytes: (fileId: string, sentBytes: number) => void;
}

export interface IDataRoomFileOutcome {
  success: boolean;
  /** A failure no retry can fix (cancelled, or the bucket hides the ETag), which stops the room. */
  isFatal: boolean;
  uploadStatus: DocumentReaderUploadStatus | null;
  error: string | null;
}

export interface ICreateDataRoomParams {
  uploads: readonly CommendaAiUploadFile[];
  limits: Readonly<IDocumentReaderUploadLimits>;
  conversationId: string;
  companyId: number | null;
  signal: AbortSignal;
  onRecordChange: (record: IDataRoomResumeRecord) => void;
}

export type TCreateDataRoomResult =
  | {
      success: true;
      uploadId: string;
      files: IDataRoomLocalFile[];
      skippedEmptyFileNames: string[];
    }
  | { success: false; error: string };

export type TLoadDataRoomResumeResult =
  | {
      success: true;
      status: DocumentReaderUploadStatus;
      files: IDataRoomLocalFile[];
      storedRows: IDataRoomFileProgress[];
      unmatchedNames: string[];
      abortedNames: string[];
    }
  | {
      success: false;
      status: DocumentReaderUploadStatus | null;
      error: string;
    };
